import type { CustomCssVariables } from '@thoughtspot/visual-embed-sdk';
import { getGroupedVariables } from '../utils/sorting';

export type stateInterface = Record<keyof CustomCssVariables, string> & {
  liveboardList: any[];
  liveboardVizList: any[];
  dataSourceList: any[];
  answerList: any[];
};

export type TsCssVariable = keyof CustomCssVariables;

export interface StyleUpdateAction {
  type: 'updateStyleState';
  value: {
    key: string;
    value: string;
  };
}

// Embed component interfaces
export interface EmbedSelections {
  pinboard: string;
  viz: string;
  dataSource: string;
  answerId: string;
  pageId: string;
  searchType: string;
}

export interface EmbedComponentProps {
  embedRef: React.RefObject<HTMLElement>;
  variables: Record<string, string>;
  embedSelections: EmbedSelections;
}

export interface ComponentSelectionProps {
  options: Array<{ id: string; label: string; disabled: boolean }>;
  selected: string;
  onChange: (value: string) => void;
  isLink?: boolean;
  isSubLink?: boolean;
}

export interface DropdownItem {
  id: number;
  title: string;
  isHidden: boolean;
  component: React.ReactNode;
}

export interface VariableSectionProps {
  variables: {
    name: string;
    variables: CssVariable[];
  }[];
}

export interface ReleaseVariables {
  variables: Record<string, string>;
}

export type IVariableAction =
  | {
      type:
        | 'color'
        | 'background'
        | 'navColor'
        | 'navBackground'
        | 'fontFamily'
        | 'textTransform'
        | 'searchBarColor'
        | 'searchDataBackground'
        | 'searchAutoBackground'
        | 'searchBarFontStyle'
        | 'searchTextFontColor'
        | 'searchTextFontFamily'
        | 'searchBarFontFamily'
        | 'searchBackground'
        | 'searchHoverBackground'
        | 'searchNavHelpBg'
        | 'searchNavBtnBg'
        | 'searchNavAutoHoverBg'
        | 'dataPanelBg'
        | 'viewTableSwitcherBg'
        | 'viewTableActiveSwitcherBg'
        | 'btnBorderRadius'
        | 'btnIconBorderRadius'
        | 'btnPrimaryBg'
        | 'btnPrimaryHoverBg'
        | 'btnSecondaryBg'
        | 'btnSecondaryHoverBg'
        | 'btnTertiaryBg'
        | 'btnTertiaryHoverBg'
        | 'answerChartSelectBg'
        | 'answerChartSelectHoverBg'
        | 'vizBorderRadius'
        | 'vizBoxShadow'
        | 'vizBackground'
        | 'vizLegendHoverBg'
        | 'chipBorderRadius'
        | 'chipBoxShadow'
        | 'chipBoxBg'
        | 'chipHoverBg'
        | 'chipActiveBg'
        | 'chipActiveColor'
        | 'menuBg'
        | 'menuHoverBg'
        | 'dialogBodyBg'
        | 'dialogHeaderBg'
        | 'dialogFooterBg'
        | 'axisFontFamily'
        | 'axisDataColor'
        | 'axisDataFontFamily'
        | 'menuColor'
        | 'menuFontFamily'
        | 'menuTextTransform'
        | 'axisTitleColor'
        | 'dialogHeaderColor'
        | 'btnPrimaryColor'
        | 'btnPrimaryFontFamily'
        | 'btnSecondaryColor'
        | 'btnSecondaryFontFamily'
        | 'btnTertiaryColor'
        | 'chipFontFamily'
        | 'chipColor'
        | 'chipHoverColor'
        | 'vizTitleColor'
        | 'vizTitleFont'
        | 'vizTitleTextTransform'
        | 'vizDescColor'
        | 'vizDescFont'
        | 'vizDescTextTransform'
        | 'dialogBodyColor'
        | 'updatedState'
        | 'spotterInputBg'
        | 'spotterPromptBg';
      value: string;
    }
  | StyleUpdateAction;

export type IVariableDispatch = (action: any) => void;

export interface IVariableContext {
  state: stateInterface;
  dispatch: IVariableDispatch;
}

export enum CssVariableType {
  Color = 'Color',
  Size = 'Size',
  Font = 'Font',
  TextTransform = 'TextTransform',
  BoxShadow = 'BoxShadow',
  Section = 'Section',
}

export enum VariableGroup {
  Application = 'Application level',
  Search = 'Search data',
  Button = 'Buttons',
  Menu = 'Menu',
  List = 'Object lists',
  Charts = 'Charts',
  Home = 'Home Page',
  Legends = 'Legends',
  Chip = 'Filter/Parameter chips',
  Dialog = 'Modal dialogs',
  Checkbox = 'Checkboxes',
  Navbar = 'Navigation bar',
  Spotter = 'Spotter',
  Liveboard = 'Liveboard',
  NavbarV3 = 'Navigation bar V3',
  NLS = 'Natural language search',
  ModularHomepage = 'Home page',
  SpotIQ = 'SpotIQ analytics',
  Advanced = 'Advanced',
}

export enum VariableComponent {
  Viz = 'Viz',
  Liveboard = 'Liveboard',
  App = 'App',
  Search = 'Search',
  Spotter = 'Spotter',
  SpotIQ = 'SpotIQ',
  Sage = 'Sage',
  Default = 'Default',
}

export enum FirstTimeViewName {
  HasSeenManualModeExplainer = 'hasSeenManualModeExplainer',
  HasSeenFileUploadExplainer = 'hasSeenFileUploadExplainer',
}

export type viewType = 'search' | 'liveboard' | 'viz' | 'spotter' | 'home';
export type popUpType = 'ROUTE_CHANGE' | 'FAILED_UPLOAD';

export enum AiResponseIssueKey {
  USER_CANCELLED_ISSUE = 'USER_CANCELLED_ISSUE',
  INTERNAL_SERVER_ISSUE = 'INTERNAL_SERVER_ISSUE',
  PROCESSING_ISSUE = 'PROCESSING_ISSUE',
  TIMEOUT_ISSUE = 'TIMEOUT_ISSUE',
  NETWORK_ISSUE = 'NETWORK_ISSUE',
}
export interface PdfFileType {
  name: string;
  text: string;
  wordCount: number;
  size: string;
  id: string;
}
export interface ImageType {
  name: string;
  size: string;
  content: string;
  id: string;
}

export enum MessageRoleType {
  USER = 'user',
  AI = 'ai',
  ISSUE = 'issue',
}
export interface MessageType {
  role: (typeof MessageRoleType)[keyof typeof MessageRoleType];
  content: string;
  id: string;
  images?: ImageType[];
  pdfFiles?: PdfFileType[];
  isManualModeActive?: boolean;
}

export enum SupportedUploadMimeType {
  Pdf = 'application/pdf',
  ImageJpeg = 'image/jpeg',
  ImageJpg = 'image/jpg',
  ImagePng = 'image/png',
  ImageWebp = 'image/webp',
  ImageGif = 'image/gif',
}

export enum AiResponseResponseType {
  DONE = 'done',
  ERROR = 'error',
  TEXT = 'text',
  SUMMARY = 'summary',
}

export enum ActiveViewEmbedType {
  liveboard = 'liveboard',
  search = 'search',
  spotter = 'spotter',
  viz = 'viz',
  home = 'full app',
}

export interface CssVariable {
  key: TsCssVariable;
  group: VariableGroup;
  subGroup?: string;
  type: CssVariableType;
  name: string;
  defaultValue?: string;
  components?: VariableComponent[];
  hidden?: boolean;
}

export type VariableGroups = ReturnType<typeof getGroupedVariables>;
